import { Loader } from '@/components'
import { Categories } from '@/enums'
import {
  CheckoutBase,
  CheckoutStep,
} from '@/modules/checkout/components/CheckoutBase'
import { usePublicCart } from '@/services'
import { PublicCartResponse } from '@/types/cart'
import {
  getLocalStorageItem,
  noop,
  pushEventToDataLayer,
  setLocalStorageItem,
  useGoToPath,
  useModal,
  useStateObject,
  useSteps,
} from '@/utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import { useForwardFunnelProduct } from '../SelfAssessmentPage/helpers'
import { SuccessPage } from '../SelfAssessmentPage/public/SuccessPage'
import { ThankYouPage } from '../SelfAssessmentPage/public/ThankYouPage'
import { getLocationIdFromToken, makeCheckoutSteps } from './helpers'
import { PublicCheckoutStepKeys } from './steps'

type CategoryId = 'LI' | 'CO' | 'LE' | 'FL' | 'CY'

const tooltips = {}

interface StepData {
  Component: React.ComponentType<any>
  key: string
  [key: string]: any
}

const useMessages = () => {
  const { categoryId } = useParams<{ categoryId: string }>()
  const { t } = useTranslation()

  return {
    yourCart: t('yourMission'),
    categoryName: t(
      `categories.${
        Categories[categoryId as keyof typeof Categories].key
      }.name`,
    ),
    next: t('next'),
    contactTitle: t('need_help'),
    contactSubtitle: t('contact_description'),
    failedAnswersTitle: t('failed_answers_title'),
    failedAnswersSubtitle: t('failed_answers_subtitle'),
    titles: {
      payment: t('checkout.titles.payment'),
      killerQuestions: t('checkout.titles.killerQuestions'),
      poa: t('checkout.titles.poa'),
      paymentAndPoa: t('checkout.titles.paymentAndPoa'),
      companyDetails: t('checkout.titles.companyDetails'),
      startDate: t('checkout.titles.startDate'),
      overview: t('checkout.titles.overview'),
      success: t('checkout.titles.success'),
    },
    breadcrumbs: {
      startDate: t('checkout.breadcrumbs.startDate'),
      payment: t('checkout.breadcrumbs.payment'),
      paymentAndPoa: t('checkout.breadcrumbs.paymentAndPoa'),
      killerQuestions: t('checkout.breadcrumbs.killerQuestions'),
      poa: t('checkout.breadcrumbs.poa'),
      companyDetails: t('checkout.breadcrumbs.companyDetails'),
      overview: t('checkout.breadcrumbs.overview'),
      success: t('checkout.breadcrumbs.success'),
    },
    subtitles: {
      startDate: t('checkout.subtitles.startDate'),
      paymentAndPoa: t('checkout.subtitles.paymentAndPoa'),
      overview: t('checkout.subtitles.overview'),
      success: t('checkout.subtitles.success'),
      companyDetails: t('checkout.subtitles.companyDetails'),
      payment: t('checkout.subtitles.payment'),
      killerQuestions: t('checkout.subtitles.killerQuestions'),
    },
    from: t('from'),
  }
}

interface PublicCheckoutPageContentProps {
  steps: CheckoutStep[]
  data?: PublicCartResponse
}

const PublicCheckoutPageContent = ({
  steps,
  data = [],
}: PublicCheckoutPageContentProps) => {
  const { categoryId } = useParams<{ categoryId: string }>()
  const { state } = useLocation() as { state?: { backPathname?: string } }
  const { backPathname } = state || {}
  const { t } = useTranslation()
  const messages = useMessages()
  const initialStepLocalStorageKey = `${categoryId}-checkout-current-step`
  const initialStep = getLocalStorageItem(initialStepLocalStorageKey)
  const locationId = useMemo(getLocationIdFromToken, [])

  const goBack = useGoToPath(backPathname || '')

  const stepState = useSteps({
    initialStep,
    steps,
    keyPropName: 'key',
  })
  const contactState = useModal()
  const successState = useModal()
  const [protocolDownloadUrl, setProtocolDownloadUrl] = useState<string>()

  const openSuccess = (downloadUrl?: string) => {
    if (downloadUrl) setProtocolDownloadUrl(downloadUrl)
    successState.open()
  }

  const [buttonState, setButtonState] = useStateObject({
    onClick: stepState.next,
    text: messages.next,
  })
  const [secondaryButtonState, setSecondaryButtonState] = useStateObject({
    onClick: noop,
    text: t('iDoNotConfirm'),
  })

  const { Component, key } = useMemo(() => stepState.data as StepData, [
    // @ts-expect-error this seems to be an error. leaving this as is to avoid logic changes when migrating to ts
    stepState.data?.key,
  ])

  const handleBack = () => {
    if (stepState.isFirst) {
      goBack()
    } else {
      stepState.back()
    }
  }

  const { funnel_product: funnelProduct } = useForwardFunnelProduct()

  const handleNext = useCallback(() => {
    setLocalStorageItem(initialStepLocalStorageKey, key)

    pushEventToDataLayer('Step Change', {
      funnel_product: funnelProduct,
      funnel_step: key,
    })

    stepState.next()
  }, [key])

  const handleGoToPayment = () => {
    stepState.goTo(PublicCheckoutStepKeys.payment)
  }

  useEffect(() => {
    if (!data.length) {
      contactState.open()
    }
  }, [data])

  if (contactState.isOpen) {
    return <SuccessPage />
  }

  if (successState.isOpen) {
    return <ThankYouPage isPublic protocolDownloadUrl={protocolDownloadUrl} />
  }

  return (
    <CheckoutBase
      buttonState={buttonState}
      secondaryButtonState={secondaryButtonState}
      data={data}
      isPublic
      steps={steps as CheckoutStep[]}
      stepState={stepState}
    >
      <Component
        funnelProduct={funnelProduct}
        goToPayment={handleGoToPayment}
        locationId={locationId}
        onBack={handleBack}
        onNext={handleNext}
        openContact={contactState.open}
        openSuccess={openSuccess}
        setButtonState={setButtonState}
        setSecondaryButtonState={setSecondaryButtonState}
      />
    </CheckoutBase>
  )
}

export const PublicCheckoutPage = () => {
  const messages = useMessages()
  const { categoryId } = useParams<{ categoryId: string }>()
  const { data, isLoading } = usePublicCart()

  if (isLoading) return <Loader />

  return (
    <PublicCheckoutPageContent
      data={data}
      steps={makeCheckoutSteps(categoryId as CategoryId, messages, tooltips)}
    />
  )
}
